import { Injectable, Inject, InjectionToken, NgZone } from '@angular/core';
import {Router} from '@angular/router';
import * as auth0 from 'auth0-js';
import {WindowRefService} from '../shared/window.service';
import {JwtHelper} from './auth.utils';
import {environment} from '../../environments/environment';
import {AuthResult, AuthToken} from './auth.config';
import {ReplaySubject} from 'rxjs';
import { AppStabilityService } from '../shared/app-stability.service';
import { HttpClient } from '@angular/common/http';
import {ga} from '../../declarations/ga';

export type Status = 'init' | 'logging_in' | 'logging_out' | 'standby' | 'logged_in' | 'logged_out';
export type LoginEvent = {
  token: string
};

export type WebAuthProvider = () => auth0.WebAuth;
export const WEBAUTH_PROVIDER = new InjectionToken<WebAuthProvider>('WebAuthProvider');

interface LoggedUserRights {
  apiReadPermissions: string[];
  apiWritePermissions: string[];
  legacyApiReadPermissions: string[];
  legacyApiWritePermissions: string[];
  legacyUiPermissions: string[];
  uiReadPermissions: string[];
  uiWritePermissions: string[];
}

/**
 * Authorization service. Communicates with Auth0 and application and LocalStorage and application.
 * Access token management, authorization, authentication etc are handled here.
 */
@Injectable()
export class AuthService {

  /**
   * JwtHelper instance
   * @type {JwtHelper}
   */
  private static jwtHelper = new JwtHelper();
  /**
   * Local reference to window object, injected via service
   */
  private _window: Window;
  private status: Status = 'init';
  private auth0: auth0.WebAuth;

  private idToken: string;
  private expiresAt: Date;
  private uiRights: Set<string> = new Set();

  private _login = new ReplaySubject<LoginEvent>(1);
  public login$ = this._login.asObservable();

  /**
   * Default constructor
   * @param {Router} router - Angular router instance
   * @param windowRef
   * @param auth0
   * @param http
   */
  constructor(
      public router: Router,
      windowRef: WindowRefService,
      @Inject(WEBAUTH_PROVIDER) authProvider: WebAuthProvider,
      appStability: AppStabilityService,
      private http: HttpClient,
      private ngZone: NgZone,
    ) {
    this.auth0 = authProvider();
    console.log('AuthService constructed');
    this._window = windowRef.nativeWindow;
    appStability.whenFirstStable().then(() => {
      console.log('App is stable, setting Auth0 renewal interval');
      setInterval(() => {
        this.renewToken(false);
      }, 30 * 60 * 1000);
      this.renewToken(false);
    });
  }

  public getToken(): string {
    return this.idToken;
  }

  public getTenant(): number {
    return this.getDecodedToken()['https://quantummaps.com/app_metadata'].tenant_id;
  }

  public getTenantKey(): string {
    return this.getDecodedToken()['https://quantummaps.com/app_metadata'].tenant_key;
  }

  public getTriTenant(): string {
    return this.getDecodedToken()['https://quantummaps.com/app_metadata'].tri_tenant;
  }

  public getSubject(): string {
    return this.getDecodedToken().sub;
  }

  public getEmail(): string {
    return this.getDecodedToken().email;
  }

  /**
   * Ask Auth0 to perform authentication.
   */
  public login(): void {
    console.log('auth login');
    this.renewToken(true);
  }

  public getStatus() {
    return this.status;
  }

  /**
   * This method is called after Auth0 login callback is returned.
   * Get token hash from URL and initialize session and navigate to default route
   * TODO navigate to route that was requested before authentication process kicked in.
   */
  public handleAuthenticationResult(): void {
    this.auth0.parseHash(async (err, authResult) => {
      console.log(authResult);
      if (authResult && authResult.accessToken && authResult.idToken) {
        await this.setSession(authResult);
        this.router.navigate(['/home']);
      } else if (err) {
        console.log(err);
        alert(`Error: ${err.error}. Check the console for further details.`);
        this.status = 'logged_out';
        this.auth0.authorize({});
      }
    });
  }

  /**
   * Check if the user has the given UI permission
   * @param {string} permission
   * @returns {boolean}
   */
  public hasPermission(permission: string): boolean {
    return this.uiRights.has(permission);
  }

  /**
   * Logout method. After logout, redirect to login. Removes all token stuff from LocalStorage.
   */
  public logout(): void {
    // Remove tokens and expiry time from localStorage (clean up for old session handling)
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('tri_tenant');
    // SSO Logout, details: https://auth0.com/docs/logout#log-out-a-user
    this._window.location.assign('https://mic.eu.auth0.com/v2/logout?returnTo=' +
      encodeURI(this._window.location.protocol + '//' + this._window.location.host));
  }

  /**
   * Check if user has valid token. Currently tests override this check.
   * @returns {boolean} - authenticated
   */
  public isAuthenticated(): boolean {
    // Check whether the current time is past the access token's expiry time
    return this.expiresAt && new Date().getTime() < this.expiresAt.getTime();
  }

  private getDecodedToken(): AuthToken {
    return AuthService.jwtHelper.decodeToken(this.idToken);
  }

  /**
   * Process an authentication result and notify subscribers of $login.
   */
  private async setSession(authResult: AuthResult): Promise<void> {
    console.log('auth setSession', authResult);

    this.idToken = authResult.idToken;
    console.log('ID token', this.getDecodedToken());

    this.expiresAt = new Date();
    this.expiresAt.setSeconds(this.expiresAt.getSeconds() + authResult.expiresIn);

    const rights = await this.http.get<LoggedUserRights>(environment.apiURL + 'loggeduserrights').toPromise();
    this.uiRights = new Set([
      rights.legacyUiPermissions || [], // contains the ui_rights from the JWT token
      rights.uiReadPermissions || [],
      rights.uiWritePermissions || [],
    ].flat().filter(right => right));

    this.status = 'logged_in';
    this._login.next({
      token: authResult.idToken
    });
  }

  /**
   * Renew authentication token
   * @param {boolean} login
   */
  private renewToken(login: boolean): void {
    this.status = 'logging_in';
    console.log('auth renewtoken');
    this.auth0.renewAuth(<any>
      {
        redirectUri: environment.silentURL,
        usePostMessage: true,
        timeout: 15000
      }, async (err, result) => {
        console.log('RENEW RESULT:', result);
        console.log('RENEW ERROR:', err);
        if (err) {
          if (!this.isAuthenticated()) {
            this.status = 'logged_out';
            return this.auth0.authorize({});
          }
          // what should be the status here?
          this.status = 'logged_in';
        } else {
          console.log('auth result', result);
          await this.setSession(result);
          if (login) {
            this.ngZone.runOutsideAngular(() => {
              const interval = setInterval(() => {
                try {
                  const _ga = ga || (window as any).ga;
                  if (_ga) {
                    console.log('Init GA');
                    clearInterval(interval);
                    _ga('create', 'UA-153296020-1', 'auto');
                    _ga('set', 'dimension1', 'TENANT_' + this.getTenant());
                  }
                } catch (e) {
                }
              }, 50);
            });

            this.router.navigate(['/home']);
          }
        }
      }
    );
  }
}
